import { supabase } from "./supabase";

export type RegimeFiscalDossier = "rni" | "rsi" | "micro" | "tee";
export type PeriodiciteTvaDossier = "mensuelle" | "trimestrielle";
export type ExigibiliteTvaDossier = "debits" | "encaissements";

export interface DossierFiscaliteState {
  annee_exercice: number;
  regime_fiscal: RegimeFiscalDossier;
  assujetti_tva: boolean;
  taux_tva_defaut: number;
  periodicite_tva: PeriodiciteTvaDossier;
  exigibilite_tva_defaut: ExigibiliteTvaDossier;
  collecteur_airsi: boolean;
  taux_airsi: number;
  taux_bic: number;
}

const REGIME_SET = new Set<string>(["rni", "rsi", "micro", "tee"]);

/**
 * Libellés choisis à l’onboarding (par pays) → `dossiers.regime_fiscal` (contrainte CHECK).
 * Valeur vide ou inconnue → `rni`.
 */
export function regimeFiscalDepuisOnboarding(
  pays: "CI" | "SN" | "CM",
  libelle: string | null | undefined
): RegimeFiscalDossier {
  const r = (libelle ?? "").trim();
  if (!r) return "rni";
  if (pays === "CI") {
    if (r === "Entreprenant" || r === "Micro-Entreprise") return "micro";
    if (r === "RSI") return "rsi";
    if (r === "RRN") return "rni";
  }
  if (pays === "SN") {
    if (r === "CGU") return "micro";
    if (r === "Réel") return "rni";
  }
  if (pays === "CM") {
    if (r === "Libératoire") return "tee";
    if (r === "Simplifié") return "rsi";
    if (r === "Réel") return "rni";
  }
  return "rni";
}
const PERIOD_SET = new Set<string>(["mensuelle", "trimestrielle"]);
const EXIG_SET = new Set<string>(["debits", "encaissements"]);

function normRegime(v: string | null | undefined): RegimeFiscalDossier {
  return v && REGIME_SET.has(v) ? (v as RegimeFiscalDossier) : "rni";
}

function normPeriod(v: string | null | undefined): PeriodiciteTvaDossier {
  return v && PERIOD_SET.has(v) ? (v as PeriodiciteTvaDossier) : "mensuelle";
}

function normExig(v: string | null | undefined): ExigibiliteTvaDossier {
  return v && EXIG_SET.has(v) ? (v as ExigibiliteTvaDossier) : "debits";
}

export async function fetchDossierFiscalite(dossierId: string): Promise<DossierFiscaliteState> {
  const { data, error } = await supabase
    .from("dossiers")
    .select(
      "annee_exercice, regime_fiscal, assujetti_tva, taux_tva_defaut, periodicite_tva, exigibilite_tva_defaut, collecteur_airsi, taux_airsi, taux_bic, tva_active, taux_tva"
    )
    .eq("id", dossierId)
    .single();

  if (error) throw error;

  const annee = new Date().getFullYear();
  const tvaRaw = Number(data?.taux_tva_defaut ?? data?.taux_tva ?? 18);
  const taux_tva_defaut = tvaRaw === 9 || tvaRaw === 18 ? tvaRaw : 18;
  const airsiRaw = Number(data?.taux_airsi ?? 5);
  const taux_airsi = [5, 2, 1.5, 0.2].includes(airsiRaw) ? airsiRaw : 5;
  const bicRaw = Number(data?.taux_bic ?? 25);
  const taux_bic = bicRaw === 30 || bicRaw === 25 ? bicRaw : 25;

  return {
    annee_exercice: data?.annee_exercice != null ? Number(data.annee_exercice) : annee,
    regime_fiscal: normRegime(data?.regime_fiscal),
    assujetti_tva: data?.assujetti_tva ?? data?.tva_active ?? false,
    taux_tva_defaut,
    periodicite_tva: normPeriod(data?.periodicite_tva),
    exigibilite_tva_defaut: normExig(data?.exigibilite_tva_defaut),
    collecteur_airsi: Boolean(data?.collecteur_airsi),
    taux_airsi,
    taux_bic,
  };
}

export async function saveDossierFiscalite(dossierId: string, p: DossierFiscaliteState): Promise<void> {
  const tauxTva = p.taux_tva_defaut === 9 || p.taux_tva_defaut === 18 ? p.taux_tva_defaut : 18;
  const tauxAirsi = [5, 2, 1.5, 0.2].includes(p.taux_airsi) ? p.taux_airsi : 5;
  const tauxBic = p.taux_bic === 30 || p.taux_bic === 25 ? p.taux_bic : 25;

  const { error } = await supabase
    .from("dossiers")
    .update({
      annee_exercice: p.annee_exercice,
      regime_fiscal: p.regime_fiscal,
      assujetti_tva: p.assujetti_tva,
      taux_tva_defaut: tauxTva,
      periodicite_tva: p.periodicite_tva,
      exigibilite_tva_defaut: p.exigibilite_tva_defaut,
      collecteur_airsi: p.collecteur_airsi,
      taux_airsi: tauxAirsi,
      taux_bic: tauxBic,
      tva_active: p.assujetti_tva,
      taux_tva: tauxTva,
    })
    .eq("id", dossierId);

  if (error) throw error;
}

export function effetsChangementRegimeFiscal(regime: RegimeFiscalDossier): Pick<
  DossierFiscaliteState,
  "assujetti_tva" | "collecteur_airsi"
> {
  if (regime === "micro" || regime === "tee") {
    return { assujetti_tva: false, collecteur_airsi: false };
  }
  return { assujetti_tva: true, collecteur_airsi: true };
}
